import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigationType, useParams, matchPath } from "react-router";
import C from "classnames";

// Toolkit imports
import { rehydrateUserAuthStatus } from "sj-ui-toolkit/src/utils/authService.js";
import { useLoginFlow } from "sj-ui-toolkit/src/hooks";
import { LearnAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { Header } from "sj-ui-toolkit/src/atlas/components";
import { UserContextProvider } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { SettingsContextProvider } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { useLearnContext } from "../../context/LearnContext";
import { LevelContextProvider, BreadcrumbProvider } from "sj-ui-toolkit/src/atlas/contexts";
import { themeClasses as T } from "sj-ui-toolkit/src/atlas/utils";

import "sj-ui-toolkit/src/atlas/styles/pathBackgrounds.scss";
import S from "./Layout.module.scss";

interface PathConfig {
  variant: string;
  map: string;
}

const defaultPathConfig: PathConfig = { variant: "mauve", map: "1" };

// Per-history-entry scroll positions, capped at SCROLL_HISTORY_LIMIT (LRU via Map's
// insertion-order iteration) so long sessions don't grow this map without bound.
const SCROLL_HISTORY_LIMIT = 50;
const scrollPositionsByKey = new Map<string, number>();

const rememberScrollPosition = (key: string, value: number) => {
  if (scrollPositionsByKey.has(key)) scrollPositionsByKey.delete(key);
  scrollPositionsByKey.set(key, value);
  while (scrollPositionsByKey.size > SCROLL_HISTORY_LIMIT) {
    const oldestKey = scrollPositionsByKey.keys().next().value;
    if (oldestKey === undefined) break;
    scrollPositionsByKey.delete(oldestKey);
  }
};

// Check authentication status and redirect to login if not authenticated
async function checkAuthAndRedirect(doRedirect: () => void) {
  const isAuthenticated = await rehydrateUserAuthStatus();
  if (!isAuthenticated) {
    doRedirect();
    throw new Error("User is not authenticated");
  }
}

const Layout = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { handleUnauthedAccess } = useLoginFlow();
  const { pathId } = useParams();
  const { isScorm } = useLearnContext();
  const [pathConfig, setPathConfig] = useState<Record<string, PathConfig>>({});
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  // Updated synchronously in render so a stale scroll listener firing between commit and
  // cleanup can detect that the route has changed and skip its (now-clamped, bogus) save.
  const currentKeyRef = useRef(location.key);
  currentKeyRef.current = location.key;

  // Check if user is authenticated, refreshing the authToken if necessary
  useEffect(() => {
    checkAuthAndRedirect(() => handleUnauthedAccess()).then(() => {
      setIsAuthChecked(true);
    });
  }, []);

  // Save scroll continuously (not on cleanup) so we don't race against the new route's DOM
  // commit clamping window.scrollY before we read it. Throttled to one save per frame.
  useEffect(() => {
    const boundKey = location.key;
    let frame: number | null = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        // Skip if a route transition is in flight: layout-shift scroll events from new
        // content can fire before our cleanup runs, with a clamped scrollY that isn't the
        // user's real position.
        if (currentKeyRef.current !== boundKey) return;
        rememberScrollPosition(boundKey, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [location.key]);

  // PUSH/REPLACE → top. POP → restore saved scroll, polling document height until it's
  // tall enough (the destination may render a short loading state first, which would
  // otherwise cause scrollTo to clamp).
  useEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
      return undefined;
    }

    const target = scrollPositionsByKey.get(location.key) ?? 0;
    if (target <= 0) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const startedAt = Date.now();
    const TIMEOUT_MS = 2000;

    const tryRestore = () => {
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      if (maxY >= target) {
        window.scrollTo(0, target);
        return true;
      }
      return false;
    };

    if (tryRestore()) return undefined;

    const intervalId = setInterval(() => {
      if (tryRestore() || Date.now() - startedAt > TIMEOUT_MS) {
        clearInterval(intervalId);
      }
    }, 50);

    return () => clearInterval(intervalId);
  }, [location.key, navigationType]);

  // This is here so we can accurately set pathConfig for path backgrounds in /path/:pathId
  useEffect(() => {
    const fetchPathConfig = async () => {
      try {
        // TODO: replace with live endpoint SHRPA-2585
        const response = await fetch("/futureEndpoints/pathMaps.json");
        if (!response.ok) {
          throw new Error(`Failed to fetch path config: ${response.status}`);
        }
        const data: Record<string, PathConfig> = await response.json();
        setPathConfig(data);
      } catch (error) {
        console.error("Error fetching path configuration:", error);
      }
    };

    fetchPathConfig();
  }, []);

  // Map routes to background classes
  const routeBgClassMap: Record<string, string> = {
    [LearnAppRoutes.route]: "atlas-bg-home",
    [LearnAppRoutes.routes.catalog.route]: "atlas-bg-home",
    [LearnAppRoutes.routes.lesson.route]: "atlas-bg-lesson",
    [LearnAppRoutes.routes.exercise.route]: "atlas-bg-lesson",
    [LearnAppRoutes.routes.scorm.route]: "atlas-bg-lesson",
    [LearnAppRoutes.routes.tournaments.routes.content.route]: "atlas-bg-lesson",
    [LearnAppRoutes.routes.tournaments.routes.details.route]: "path-bg-1-mauve",
    [LearnAppRoutes.routes.testConnection.route]: "atlas-bg-home",
    [LearnAppRoutes.routes.legacyBeltRedirect.route]: "atlas-bg-lesson",
    [LearnAppRoutes.routes.legacyLessonRedirect.route]: "atlas-bg-lesson",
    [LearnAppRoutes.routes.personalizedLessonAssignment.route]: "path-bg-2-indigo",
  };

  // Loop over routeBGClassMap to find a matching route
  // Path is different due to the fact that we have 32 background variants for that page
  let bgClass = "atlas-bg-home";
  for (const route in routeBgClassMap) {
    if (matchPath(route, location.pathname)) {
      bgClass = routeBgClassMap[route]!;
      break;
    } else if (matchPath(LearnAppRoutes.routes.path.route, location.pathname)) {
      const individualPathConfig = (pathId && pathConfig[pathId]) || defaultPathConfig;
      const { variant, map } = individualPathConfig;

      bgClass = `path-bg-${map}-${variant}`;
    }
  }

  // Determine which content container to use
  const isLessonBg = bgClass === "atlas-bg-lesson";
  const contentContainerClass = isLessonBg ? T.lessonContentContainer : T.mainContentContainer;

  // Don't render the layout until we've checked auth, to avoid flash of content for unauthenticated users
  if (!isAuthChecked) {
    return null;
  }

  return (
    <div className={C(bgClass, !isLessonBg && "atlas-bg-content-inset", T.flexLayout)}>
      <SettingsContextProvider>
        <UserContextProvider>
          <BreadcrumbProvider appRoot="learn">
            <LevelContextProvider>
              {!isScorm && <Header showOfflineBanner />}
              <main
                id="main-content"
                className={C(isScorm && S.addTopPadding, S.safeArea, T.flexMain)}
              >
                <div className={contentContainerClass}>
                  <Outlet />
                </div>
              </main>
            </LevelContextProvider>
          </BreadcrumbProvider>
        </UserContextProvider>
      </SettingsContextProvider>
    </div>
  );
};

export default Layout;
